import { CARD_ACTIVATE_MAX, type CardListItem, PRINT_BATCH_MAX, PAGE_SIZE_DEFAULT } from "@seagames/contracts";
import { DataTable, IconButton, Icons, KeysetPager, ListToolbar, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CheckIcon, PrinterIcon, SlidersIcon } from "../../components/accreditation/icons";
import { readableOrganisations } from "../../components/accreditation/model";
import { ActivateSheet, PrintBatchSheet, type SheetTarget } from "../../components/e-accreditation/bulk-sheets";
import { useCardColumns } from "../../components/e-accreditation/columns";
import { CardFilterSheet, type CardFilters, cardFilterCount } from "../../components/e-accreditation/filter-sheet";
import { LookupSheet } from "../../components/e-accreditation/lookup-sheet";
import { CARD_PAGE_SIZES, cardStatesParam, looksActivatable, looksPrintable, parseCardStates, splitBy, stateCount, visibleStates } from "../../components/e-accreditation/model";
import { CardStateDot, CardStatesGuide } from "../../components/e-accreditation/parts";
import { type CardsWireFilter, cardsListOptions, cardsSummaryOptions, printBatchOptions } from "../../lib/api/e-accreditation";
import { gamesDetailOptions, useDefaultGamesId } from "../../lib/competition/games";
import { canActivateCards, canLookupCards, canPrintCards, canSeePrintBatches, canSelectCards } from "../../lib/e-accreditation/access";
import { keysetSearch, searchText } from "../../lib/list-page/list-search";
import { useListPage } from "../../lib/list-page/use-list-page";

/**
 * Cards (her `eCards`, admin.js:1568). URL = the list's state in the API's
 * own keys (`cardsListQuery`): `card_state` (comma list), organisation,
 * function, sport, print batch, `q`, plus the keyset `cursor` / `prev` and
 * `limit`. Keyset by card number, newest first — her Name A–Z sort is not
 * offered (a name sort over 60k cards is not a keyset the API serves).
 *
 * Bulk: select rows (or "Select all N" — the filter plus the count seen,
 * re-counted by the server) → Activate / Add to print batch, for the host
 * side only; the NOC reads.
 */
export const Route = createFileRoute("/_app/accreditation/e-accreditation/cards")({
	validateSearch: keysetSearch({
		games_id: z.string().uuid(),
		card_state: z.string().max(200),
		responsible_organization_id: z.string().uuid(),
		main_function_id: z.string().uuid(),
		sport_id: z.string().uuid(),
		print_batch_id: z.string().uuid(),
		q: searchText(),
	}),
	component: CardsPage,
});

function CardsPage() {
	const { t } = useTranslation("eAccreditation");
	const { t: tc } = useTranslation("common");
	const { formatNumber: fmt } = useUiStrings();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const goTo = useNavigate();
	const { session } = Route.useRouteContext();
	const gamesId = useDefaultGamesId(search.games_id);
	const games = useQuery({ ...gamesDetailOptions(gamesId ?? ""), enabled: !!gamesId });
	const canActivate = canActivateCards(session);
	const canPrint = canPrintCards(session);
	const selectable = canSelectCards(session);
	const orgScope = useMemo(() => readableOrganisations(session), [session]);
	const showOrgFilter = orgScope.show || !!search.responsible_organization_id;

	// ── URL → wire ────────────────────────────────────────────────────────
	const states = parseCardStates(search.card_state);
	const filter: CardsWireFilter = {
		games_id: gamesId ?? "",
		card_state: cardStatesParam(states),
		responsible_organization_id: search.responsible_organization_id,
		main_function_id: search.main_function_id,
		sport_id: search.sport_id,
		print_batch_id: search.print_batch_id,
		q: search.q,
	};
	const filterKey = JSON.stringify(filter);
	const applied: CardFilters = {
		responsible_organization_id: search.responsible_organization_id ?? "",
		main_function_id: search.main_function_id ?? "",
		sport_id: search.sport_id ?? "",
	};

	const summary = useQuery(cardsSummaryOptions(gamesId, search.responsible_organization_id));
	// The batch chip names its batch (the batch read is the printers'; anyone else sees the generic label).
	const batch = useQuery({ ...printBatchOptions(search.print_batch_id ?? ""), enabled: !!search.print_batch_id && canSeePrintBatches(session) });
	const batchName = batch.data?.name;
	const page = useListPage({
		paging: "keyset",
		search,
		navigate,
		listOptions: (q) => cardsListOptions({ ...filter, ...q }),
		columnsKey: "e-accreditation-cards",
		pageSizes: CARD_PAGE_SIZES,
		defaultPageSize: PAGE_SIZE_DEFAULT,
		summary: {
			showing: (v) => t("cards.pager.showing", v),
			showingNoTotal: (v) => t("cards.pager.showingNoTotal", v),
			empty: t("cards.pager.empty"),
		},
	});
	const total = page.total;
	const filterCount = cardFilterCount(applied);
	const hasAnyFilter = filterCount > 0 || states.length > 0 || !!search.q || !!search.print_batch_id;
	// The chip counts come from the summary (games + organisation); with any other filter on they would mislead.
	const countsFit = !search.main_function_id && !search.sport_id && !search.print_batch_id && !search.q;

	const clearAll = () =>
		page.patch({ card_state: undefined, responsible_organization_id: undefined, main_function_id: undefined, sport_id: undefined, print_batch_id: undefined, q: undefined });

	// ── Selection: survives paging, resets when the result set changes ─────
	const [selected, setSelected] = useState<Map<string, CardListItem>>(new Map());
	const [allMatching, setAllMatching] = useState(false);
	useEffect(() => {
		setSelected(new Map());
		setAllMatching(false);
	}, [filterKey]);
	const clearSelection = () => {
		setSelected(new Map());
		setAllMatching(false);
	};
	const selectionCount = allMatching ? (total ?? 0) : selected.size;
	const selectedRows = [...selected.values()];
	const bulkMax = Math.min(CARD_ACTIVATE_MAX, PRINT_BATCH_MAX);

	// ── Sheets ─────────────────────────────────────────────────────────────
	const [filtersOpen, setFiltersOpen] = useState(false);
	const [lookupOpen, setLookupOpen] = useState(false);
	const [activateTarget, setActivateTarget] = useState<SheetTarget | null>(null);
	const [printTarget, setPrintTarget] = useState<SheetTarget | null>(null);
	const sheetTarget = (): SheetTarget => (allMatching ? { kind: "filter", filter, expectedCount: total ?? 0 } : { kind: "rows", rows: selectedRows });

	const columns = useCardColumns(search.games_id);
	const activatable = allMatching ? null : splitBy(selectedRows, (r) => looksActivatable(r.card_state));
	const printable = allMatching ? null : splitBy(selectedRows, looksPrintable);

	const pager = (position: "top" | "bottom") =>
		page.list.isError || (position === "bottom" && page.rows.length === 0) ? null : <KeysetPager {...page.pager} position={position} />;

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			<div className="flex flex-wrap items-end justify-between gap-[0.875rem]">
				<p className="text-[0.875rem] text-gms-ink-2 tabular-nums">{total === null ? " " : t("cards.count", { count: total, n: fmt(total) })}</p>
				{canLookupCards(session) && (
				<button
					type="button"
					onClick={() => setLookupOpen(true)}
					className="btn h-[2.625rem] min-h-[2.625rem] gap-[0.5rem] rounded-[0.5rem] border-gms-line-2 bg-base-100 px-[0.875rem] text-[0.875rem] font-semibold text-gms-navy shadow-none hover:border-gms-navy [&>svg]:size-[1.125rem]"
				>
					<Icons.Search />
					{t("cards.find")}
				</button>
				)}
			</div>

			<ListToolbar
				search={search.q ?? ""}
				onSearch={(v) => page.patch({ q: v || undefined })}
				searchPlaceholder={t("cards.search")}
				afterSearch={
					<button
						type="button"
						onClick={() => setFiltersOpen(true)}
						aria-label={filterCount > 0 ? t("cards.filtersCount", { n: fmt(filterCount) }) : t("cards.filters")}
						className={`inline-flex h-[2.875rem] shrink-0 cursor-pointer items-center justify-center gap-[0.5rem] rounded-[0.5rem] border px-[0.875rem] text-[0.875rem] font-semibold transition-colors [&>svg]:size-[1.125rem] max-[45rem]:w-[2.875rem] max-[45rem]:px-0 ${
							filterCount > 0 ? "border-gms-navy bg-gms-navy-soft text-gms-navy" : "border-gms-line-2 bg-base-100 text-gms-navy-ink hover:border-gms-navy [&>svg]:text-gms-ink-2"
						}`}
					>
						<SlidersIcon />
						<span className="max-[45rem]:hidden">{filterCount > 0 ? t("cards.filtersCount", { n: fmt(filterCount) }) : t("cards.filters")}</span>
					</button>
				}
				end={<CardStatesGuide />}
				chips={[
					{
						kind: "options",
						key: "state",
						label: t("cards.chips.states"),
						multiple: true,
						options: visibleStates(summary.data, states).map((s) => ({
							value: s,
							label: t(`state.${s}`),
							icon: <CardStateDot state={s} />,
							count: countsFit && summary.data ? stateCount(summary.data, s) : undefined,
						})),
						value: states,
						onChange: (v) => page.patch({ card_state: cardStatesParam(parseCardStates(v.join(","))) }),
					},
					// Shown only while the URL carries it (the Print batches tab links here) — a filter is never invisible.
					...(search.print_batch_id
						? [
								{
									kind: "toggle" as const,
									key: "batch",
									icon: <PrinterIcon className="size-[1rem]" />,
									label: batchName ?? t("cards.chips.batch"),
									pressed: true,
									onChange: (on: boolean) => page.patch({ print_batch_id: on ? search.print_batch_id : undefined }),
								},
							]
						: []),
				]}
				active={hasAnyFilter}
				onClearAll={clearAll}
			/>

			{selectable && selectionCount > 0 && (
				<div
					role="region"
					aria-label={t("cards.bulk.selected", { count: selectionCount, n: fmt(selectionCount) })}
					className="flex flex-wrap items-center gap-x-[0.75rem] gap-y-[0.5rem] rounded-[0.625rem] bg-gms-navy px-[0.875rem] py-[0.5rem] text-white"
				>
					<span className="grid">
						<span className="text-[0.84375rem] font-semibold" aria-live="polite">
							{allMatching ? t("cards.bulk.allSelected", { count: selectionCount, n: fmt(selectionCount) }) : t("cards.bulk.selected", { count: selectionCount, n: fmt(selectionCount) })}
						</span>
						{canActivate && activatable && (
							<span className="text-[0.75rem] text-white/75">
								{activatable.skipped === 0
									? t("cards.bulk.allActivatable")
									: t("cards.bulk.activatableSplit", { taken: fmt(activatable.taken), skipped: fmt(activatable.skipped), count: activatable.skipped })}
							</span>
						)}
					</span>
					{!allMatching && total !== null && total > selected.size && total <= bulkMax && (
						<button type="button" onClick={() => setAllMatching(true)} className="text-[0.84375rem] font-semibold text-gms-nav-yellow hover:underline">
							{t("cards.bulk.selectAll", { count: total, n: fmt(total) })}
						</button>
					)}
					<div className="ml-auto flex flex-wrap items-center gap-[0.375rem]">
						{canPrint && (
							<BulkButton
								onClick={() => setPrintTarget(sheetTarget())}
								icon={<PrinterIcon />}
								disabled={(printable !== null && printable.taken === 0) || selectionCount > PRINT_BATCH_MAX}
								title={selectionCount > PRINT_BATCH_MAX ? t("bulk.tooMany", { max: fmt(PRINT_BATCH_MAX) }) : printable?.taken === 0 ? t("cards.bulk.nonePrintable") : undefined}
							>
								{t("cards.bulk.addToBatch")}
							</BulkButton>
						)}
						{canActivate && (
							<BulkButton
								onClick={() => setActivateTarget(sheetTarget())}
								icon={<CheckIcon />}
								primary
								disabled={(activatable !== null && activatable.taken === 0) || selectionCount > CARD_ACTIVATE_MAX}
								title={selectionCount > CARD_ACTIVATE_MAX ? t("bulk.tooMany", { max: fmt(CARD_ACTIVATE_MAX) }) : activatable?.taken === 0 ? t("cards.bulk.noneActivatable") : undefined}
							>
								{t("cards.bulk.activate", { count: activatable?.taken ?? selectionCount, n: fmt(activatable?.taken ?? selectionCount) })}
							</BulkButton>
						)}
						<button
							type="button"
							onClick={clearSelection}
							aria-label={t("cards.bulk.clear")}
							title={t("cards.bulk.clear")}
							className="grid size-[2rem] place-items-center rounded-[0.375rem] border border-white/25 text-white/85 hover:bg-white/10"
						>
							<svg aria-hidden viewBox="0 0 24 24" fill="none" className="size-[0.875rem]">
								<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
							</svg>
						</button>
					</div>
				</div>
			)}

			<DataTable<CardListItem, string>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.ac_card_id}
				pager={pager}
				selection={
					selectable
						? {
								selected: new Set(selected.keys()),
								onChange: (next) => {
									setAllMatching(false);
									setSelected((prev) => {
										const out = new Map<string, CardListItem>();
										for (const id of next) {
											const row = prev.get(id) ?? page.rows.find((r) => r.ac_card_id === id);
											if (row) out.set(id, row);
										}
										return out;
									});
								},
							}
						: undefined
				}
				rowActions={(r) => (
					<IconButton
						label={t("cards.row.open", { name: r.printed_name })}
						icon={<Icons.Eye />}
						variant="row"
						onClick={() => void goTo({ to: "/accreditation/e-accreditation/$cardId", params: { cardId: r.ac_card_id }, search: { games_id: search.games_id } })}
					/>
				)}
				empty={
					hasAnyFilter
						? {
								title: t("cards.empty.filteredTitle"),
								description: t("cards.empty.filteredDescription"),
								action: (
									<button type="button" className="btn btn-sm btn-outline" onClick={clearAll}>
										{t("cards.empty.clearFilters")}
									</button>
								),
							}
						: { title: t("cards.empty.noneTitle"), description: t("cards.empty.noneDescription") }
				}
				messages={{ errorTitle: tc("dataTable.errorTitle") }}
			/>

			<CardFilterSheet
				open={filtersOpen}
				onClose={() => setFiltersOpen(false)}
				applied={applied}
				onApply={(f) =>
					page.patch({
						responsible_organization_id: f.responsible_organization_id || undefined,
						main_function_id: f.main_function_id || undefined,
						sport_id: f.sport_id || undefined,
					})
				}
				gamesId={gamesId}
				showOrganisation={showOrgFilter}
				organisationIds={orgScope.ids}
			/>
			<LookupSheet
				open={lookupOpen}
				gamesId={gamesId}
				canActivate={canActivate}
				onClose={() => setLookupOpen(false)}
				onActivate={(row) => setActivateTarget({ kind: "card", row })}
			/>
			<ActivateSheet target={activateTarget} gamesEndsOn={games.data?.ends_on} onClose={() => setActivateTarget(null)} onDone={clearSelection} />
			<PrintBatchSheet target={printTarget} onClose={() => setPrintTarget(null)} onDone={clearSelection} />
		</div>
	);
}

function BulkButton({ children, onClick, icon, disabled, title, primary = false }: { children: ReactNode; onClick: () => void; icon?: ReactNode; disabled?: boolean; title?: string; primary?: boolean }) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			title={title}
			className={`inline-flex h-[2rem] items-center gap-[0.375rem] rounded-[0.375rem] px-[0.75rem] text-[0.8125rem] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 [&>svg]:size-[0.9375rem] ${
				primary ? "bg-white text-gms-navy hover:bg-white/90" : "bg-white/12 text-white hover:bg-white/20"
			}`}
		>
			{icon}
			{children}
		</button>
	);
}
